import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, Users, Mail, Phone, Calendar,
  DollarSign, Zap, MessageSquare, Activity, Clock,
  TrendingUp, AlertCircle, CheckCircle, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchClientDetail();
  }, [clientId]);

  const fetchClientDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClient(res.data);
    } catch (error) {
      console.error('Failed to fetch client:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      } else if (error.response?.status === 404) {
        toast.error('Client not found');
        navigate('/super-admin');
      } else {
        toast.error('Failed to load client data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async (newPlan) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${BACKEND_URL}/api/super-admin/clients/${clientId}/plan`,
        { plan: newPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plan changed to ${newPlan}`);
      fetchClientDetail();
    } catch (error) {
      toast.error('Failed to change plan');
    }
  };

  const handleSuspend = async () => {
    if (!window.confirm('Are you sure you want to suspend this client?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${BACKEND_URL}/api/super-admin/clients/${clientId}/suspend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Client suspended');
      fetchClientDetail();
    } catch (error) {
      toast.error('Failed to suspend client');
    }
  };

  const getPlanBadge = (plan) => {
    const styles = {
      free: 'bg-gray-500/20 text-gray-300',
      starter: 'bg-blue-500/20 text-blue-400',
      growth: 'bg-emerald-500/20 text-emerald-400',
      agency: 'bg-amber-500/20 text-amber-400'
    };
    return styles[plan] || styles.free;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUsagePercent = (used, limit) => {
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!client) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'leads', label: 'Leads' },
    { id: 'usage', label: 'Usage' },
    { id: 'billing', label: 'Billing' },
    { id: 'activity', label: 'Activity' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0f1e] p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/super-admin')}
            className="flex items-center gap-2 text-[#94a3b8] hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </button>
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-white">{client.workspace?.business_name}</h1>
            <Badge className={getPlanBadge(client.workspace?.plan)}>
              {client.workspace?.plan || 'Free'}
            </Badge>
          </div>
          <div className="flex items-center gap-6 mt-2 text-[#94a3b8]">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {client.owner?.email}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Joined {formatDate(client.workspace?.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Last active {formatDate(client.owner?.last_login)}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <select
            value={client.workspace?.plan || 'free'}
            onChange={(e) => handleChangePlan(e.target.value)}
            className="px-4 py-2 bg-[#1a2235] border border-[#1e3a5f] rounded-lg text-white"
          >
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="growth">Growth</option>
            <option value="agency">Agency</option>
          </select>
          <Button
            onClick={handleSuspend}
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
          >
            Suspend
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-[#1e3a5f30] pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-[#94a3b8] hover:text-white hover:bg-[#1e3a5f30]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-violet-500/20 rounded-xl">
                  <Users className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{client.leads?.total || 0}</p>
                  <p className="text-[#94a3b8]">Total Leads</p>
                </div>
              </div>
              <div className="flex gap-4 mt-4 pt-4 border-t border-[#1e3a5f30]">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-[#94a3b8] text-sm">Hot: {client.leads?.hot || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                  <span className="text-[#94a3b8] text-sm">Warm: {client.leads?.warm || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-[#94a3b8] text-sm">Cold: {client.leads?.cold || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-500/20 rounded-xl">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{client.usage?.emails_sent || 0}</p>
                  <p className="text-[#94a3b8]">Emails Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-500/20 rounded-xl">
                  <Phone className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{client.usage?.calls_made || 0}</p>
                  <p className="text-[#94a3b8]">Calls Made</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Leads */}
          <Card className="bg-[#1a2235] border-[#1e3a5f30] lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-white">Recent Leads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {client.leads?.recent?.map((lead, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#0a0f1e] rounded-lg">
                    <div>
                      <p className="text-white font-medium">{lead.name}</p>
                      <p className="text-xs text-[#94a3b8]">{lead.email || lead.phone}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={lead.score >= 70 ? 'bg-red-500/20 text-red-400' : lead.score >= 40 ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}>
                        Score: {lead.score || 0}
                      </Badge>
                      <span className="text-[#94a3b8] text-sm">{lead.stage}</span>
                    </div>
                  </div>
                ))}
                {(!client.leads?.recent || client.leads.recent.length === 0) && (
                  <p className="text-[#94a3b8] text-center py-4">No leads yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'leads' && (
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardHeader>
            <CardTitle className="text-white">All Leads ({client.leads?.total || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {client.leads?.recent?.map((lead, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-[#0a0f1e] rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-medium">
                      {lead.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{lead.name}</p>
                      <p className="text-xs text-[#94a3b8]">{lead.email} • {lead.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-white font-medium">Score: {lead.score || 0}</p>
                      <p className="text-xs text-[#94a3b8]">{lead.stage}</p>
                    </div>
                    <span className="text-[#94a3b8] text-sm">{formatDate(lead.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'usage' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {['leads_count', 'emails_sent', 'whatsapp_sent', 'calls_made'].map((metric) => {
            const labels = {
              leads_count: 'Leads',
              emails_sent: 'Emails',
              whatsapp_sent: 'WhatsApp',
              calls_made: 'Calls'
            };
            const limitKey = metric === 'leads_count' ? 'leads' : metric.replace('_sent', '').replace('_made', '');
            const used = client.usage?.[metric] || 0;
            const limit = client.limits?.[limitKey] || 100;
            const percent = getUsagePercent(used, limit);

            return (
              <Card key={metric} className="bg-[#1a2235] border-[#1e3a5f30]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-medium">{labels[metric]}</h3>
                    <span className="text-[#94a3b8]">{used} / {limit}</span>
                  </div>
                  <div className="w-full h-3 bg-[#0a0f1e] rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getUsageColor(percent)} transition-all`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <p className="text-xs text-[#94a3b8] mt-2">{percent}% used</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardHeader>
              <CardTitle className="text-white">Current Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Badge className={`${getPlanBadge(client.workspace?.plan)} text-lg px-4 py-2`}>
                    {client.workspace?.plan || 'Free'}
                  </Badge>
                  <p className="text-[#94a3b8] mt-2">Monthly subscription</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">
                    ₹{client.billing?.mrr || 0}
                  </p>
                  <p className="text-[#94a3b8]">per month</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardHeader>
              <CardTitle className="text-white">Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              {client.billing?.payments?.length > 0 ? (
                <div className="space-y-3">
                  {client.billing.payments.map((payment, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#0a0f1e] rounded-lg">
                      <div>
                        <p className="text-white">₹{payment.amount / 100}</p>
                        <p className="text-xs text-[#94a3b8]">{formatDate(payment.created_at)}</p>
                      </div>
                      <Badge className={payment.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                        {payment.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#94a3b8] text-center py-4">No payments yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'activity' && (
        <Card className="bg-[#1a2235] border-[#1e3a5f30]">
          <CardHeader>
            <CardTitle className="text-white">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {client.activity?.length > 0 ? (
              <div className="space-y-3">
                {client.activity.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 bg-[#0a0f1e] rounded-lg">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <Activity className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-white">{item.action || item.type}</p>
                      <p className="text-xs text-[#94a3b8]">{formatDate(item.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#94a3b8] text-center py-8">No activity recorded</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SuperAdminClientDetail;
